import { useLocalStorage } from "@vueuse/core";
import { createI18n } from 'vue-i18n'

type AppLocale = 'de' | 'en'
type LocaleMessages = Record<string, unknown>

const fallbackLocale: AppLocale = 'en'
const locale = useLocalStorage<AppLocale>("locale", "de");
const loadedLocales = new Set<AppLocale>()
const localeModules = import.meta.glob<{ default: LocaleMessages }>('../locales/*.json')

const normalizeLocale = (value: string | null | undefined): AppLocale => value === 'en' ? 'en' : 'de'

const i18n = createI18n({
    legacy: false,
    locale: normalizeLocale(locale.value),
    fallbackLocale,
    messages: {},
    numberFormats: {
        en: {
            currency: {
                style: "currency",
                currency: "EUR"
            },
            percent: {
                style: "percent"
            },
            decimal: {
                style: "decimal"
            }
        },
        de: {
            currency: {
                style: "currency",
                currency: "EUR"
            },
            percent: {
                style: "percent"
            },
            decimal: {
                style: "decimal"
            },
        }
    }
})

export const loadLocaleMessages = async (requestedLocale: string): Promise<AppLocale> => {
    const normalizedLocale = normalizeLocale(requestedLocale)

    if (loadedLocales.has(normalizedLocale)) {
        return normalizedLocale
    }

    const loader = localeModules[`../locales/${normalizedLocale}.json`]
    if (!loader) {
        throw new Error(`Missing locale bundle for ${normalizedLocale}`)
    }

    const messages = await loader()
    i18n.global.setLocaleMessage(normalizedLocale, messages.default)
    loadedLocales.add(normalizedLocale)

    return normalizedLocale
}

export const setAppLocale = async (requestedLocale: string): Promise<AppLocale> => {
    const normalizedLocale = await loadLocaleMessages(requestedLocale)

    if (normalizedLocale !== fallbackLocale) {
        await loadLocaleMessages(fallbackLocale)
    }

    i18n.global.locale.value = normalizedLocale
    locale.value = normalizedLocale

    if (typeof document !== 'undefined') {
        document.documentElement.lang = normalizedLocale
    }

    return normalizedLocale
}

export const initializeI18n = async (): Promise<void> => {
    await setAppLocale(locale.value)
}

export default i18n;